<template>
  <div class="dynamic-content">
    <div v-if="loading" class="dynamic-loading">
      <div class="loading-spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>
    
    <div v-else-if="error" class="dynamic-error">
      <p>{{ error }}</p>
      <button @click="$emit('retry')" class="btn btn-secondary">{{ $t('common.retry') }}</button>
    </div>
    
    <div v-else-if="componentName === 'tasks'" class="component-container">
      <ProjectWorkspace :projectId="projectId" :initialProject="project" @return-to-project="$emit('return-to-project')" defaultActiveTab="tasks" />
    </div>
    
    <div v-else-if="componentName === 'phases'" class="component-container">
      <ProjectWorkspace :projectId="projectId" :initialProject="project" @return-to-project="$emit('return-to-project')" defaultActiveTab="phases" />
    </div>
    
    <div v-else-if="componentName === 'workspace'" class="component-container">
      <ProjectWorkspace :projectId="projectId" :initialProject="project" @return-to-project="$emit('return-to-project')" />
    </div>
    
    <div v-else-if="componentName === 'photos'" class="component-container">
      <PhotoGallery :projectId="projectId" />
    </div>
    
    <div v-else-if="componentName === 'documents'" class="component-container">
      <ProjectDocuments :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>
    
    <div v-else-if="componentName === 'design'" class="component-container">
      <ProjectDesign :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>
    
    <div v-else-if="componentName === 'budget'" class="component-container">
      <ProjectFinance :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>

    <div v-else-if="componentName === 'issues'" class="component-container">
      <DeliveryIssues :project-id="projectId" />
    </div>
    
    <!-- Redirect intervenants to workspace -->
    <div v-else-if="componentName === 'intervenants'" class="component-container">
      <ProjectWorkspace :projectId="projectId" :initialProject="project" @return-to-project="$emit('return-to-project')" defaultActiveTab="intervenants" />
    </div>
    
    <div v-else-if="componentName === 'notes'" class="component-container">
      <ProjectNotes :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>
    
    <div v-else-if="componentName === 'access'" class="component-container">
      <ProjectAccess :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>
    
    <div v-else-if="componentName === 'notifications'" class="component-container">
      <ProjectNotifications :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>
    
    <div v-else-if="componentName === 'report'" class="component-container">
      <BudgetConsumptionReport :projectId="projectId" />
    </div>
    
    <div v-else-if="componentName === 'audit'" class="component-container">
      <ProjectAuditTrail :projectId="projectId" @return-to-project="$emit('return-to-project')" />
    </div>

    <div v-else-if="componentName === 'planning'" class="component-container">
      <ProjectPlanning :projectId="projectId" :project="project" />
    </div>
    
    <div v-else class="component-container empty-component">
      <svg viewBox="0 0 24 24" width="80" height="80">
        <path fill="var(--neutral-300)" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
      </svg>
      <p class="empty-text">{{ $t('projects.selectAction') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref } from 'vue';
import { PhotoGallery, ProjectDocuments, ProjectDesign, ProjectFinance, ProjectAccess, ProjectNotifications, DeliveryIssues, ProjectAuditTrail } from './index';
import BudgetConsumptionReport from './reports/BudgetConsumptionReport.vue';
import ProjectNotes from './notes/ProjectNotes.vue';
import ProjectWorkspace from './workspace/ProjectWorkspace.vue';
import ProjectPlanning from './planning/ProjectPlanning.vue';

interface Props {
  componentName: string;
  projectId: number | string | null;
  project?: any; // Project data from parent to avoid redundant API calls
}

interface Emits {
  (e: 'retry'): void;
  (e: 'return-to-project'): void;
}

const props = withDefaults(defineProps<Props>(), {
  componentName: '',
  projectId: null,
  project: null
});

const emit = defineEmits<Emits>();

const loading = ref<boolean>(false);
const error = ref<string | null>(null);

// In a real implementation, we would load data based on the component name
// For example:
/*
const loadData = async () => {
  if (!props.componentName || !props.projectId) return;

  loading.value = true;
  error.value = null;

  try {
    // Example API call based on component type
    let data;
    switch (props.componentName) {
      case 'tasks':
        data = await ProjectService.getProjectTasks(props.projectId);
        break;
      case 'photos':
        data = await ProjectService.getProjectPhotos(props.projectId);
        break;
      // And so on for other component types
      default:
        data = null;
    }

    hasContent.value = data && (Array.isArray(data) ? data.length > 0 : true);
  } catch (err) {
    console.error('Error loading component data:', err);
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};

// Watch for changes to load data when component or project changes
watch(
  () => [props.componentName, props.projectId],
  () => {
    if (props.componentName && props.projectId) {
      loadData();
    }
  },
  { immediate: true }
);
*/
</script>

<style scoped>
.dynamic-content {
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.dynamic-loading, .dynamic-error, .empty-component {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  text-align: center;
  padding: 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200);
  border-left-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.component-title {
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--neutral-200);
  padding-bottom: 0.75rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-state svg {
  margin-bottom: 1.5rem;
}

.empty-state p {
  margin-bottom: 1.5rem;
  color: var(--text-secondary);
}

.empty-text {
  font-size: 1.125rem;
  color: var(--text-secondary);
  margin-top: 1.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: var(--primary);
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn-secondary {
  background-color: var(--neutral-100);
  color: var(--text-primary);
  border: 1px solid var(--neutral-300);
}

.btn-secondary:hover {
  background-color: var(--neutral-200);
}
</style>
